import type { ComponentProps } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { cn } from '../../lib/utils';
import { ExportIcon } from '../../custom/export-icon';

export default function SuffixTextField({
	label,
	suffix,
	readOnlyHref,
	className,
	disabled,
	...props
}: ComponentProps<'input'> & {
	label: string;
	suffix: string;
	readOnlyHref?: string;
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isReadOnly = props.readOnly ?? formReadOnly;
	const showAsLink = isReadOnly && readOnlyHref;
	const isOptional = useIsFieldOptional(field.name);

	return (
		<div className={className}>
			<div className="grid gap-2">
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
					/>
				)}
				<div
					className={cn(
						'flex h-8 items-center rounded-lg border bg-transparent text-base transition-[color,border-color,padding,box-shadow] md:text-sm has-[[aria-invalid=true]]:ring-destructive/20 dark:has-[[aria-invalid=true]]:ring-destructive/40 has-[[aria-invalid=true]]:border-destructive has-[[aria-invalid=true]]:ring-3',
						isReadOnly
							? 'border-transparent px-0'
							: disabled
								? 'border-input/30 bg-[#35433f]/5 px-2.5 cursor-not-allowed'
								: 'border-input focus-within:border-ring px-2.5',
					)}
				>
					{showAsLink ? (
						<a
							href={readOnlyHref}
							target="_blank"
							rel="noopener noreferrer"
							className="text-foreground inline-flex items-center gap-1.5 underline underline-offset-2"
						>
							<span suppressHydrationWarning>
								{field.state.value}
								{suffix}
							</span>
							<ExportIcon className="text-foreground size-4" />
						</a>
					) : (
						<>
							<input
								id={field.name}
								name={field.name}
								aria-invalid={hasFieldError(field.state.meta)}
								value={isReadOnly && !field.state.value ? '-' : (field.state.value ?? '')}
								onBlur={field.handleBlur}
								onChange={(e) => field.handleChange(e.target.value)}
								{...props}
								disabled={disabled}
								readOnly={isReadOnly}
								tabIndex={isReadOnly || disabled ? -1 : undefined}
								className={cn(
									'min-w-0 flex-1 bg-transparent outline-none',
									disabled && 'text-muted-foreground cursor-not-allowed',
								)}
							/>
							<span className="text-muted-foreground shrink-0" suppressHydrationWarning>
								{suffix}
							</span>
						</>
					)}
				</div>
				<FieldInfo fieldState={field.state} />
			</div>
		</div>
	);
}
